import { ENV_REACT_APP } from "../Configurations/ENV_Config";

// Wrapped in a function so tests can mutate process.env between cases.
const apiKey = (): string =>
  ENV_REACT_APP?.REACT_APP_GOOGLE_MAPS_API_KEY ||
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  "";

/**
 * Defensive coordinate normaliser. Returns null if either value isn't a
 * finite number, the pair is the well-known "no geocode" placeholder
 * (0, 0 — REAPI / MLS often emit that for unresolved addresses), or the
 * pair is outside the legal lat/lng range. Otherwise returns numeric
 * { lat, lng }. Accepts numbers OR numeric strings.
 */
export const normalizeCoords = (
  rawLat: unknown,
  rawLng: unknown
): { lat: number; lng: number } | null => {
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;
  return { lat, lng };
};

/**
 * Google Static Maps satellite image URL. `scale=2` doubles the
 * resolution so high-DPI displays don't render blurry. Zoom 19 is
 * residential roof scale — closer than 20 starts to show pixel mush.
 */
export const satelliteImageUrl = (
  lat: number,
  lng: number,
  width = 800,
  height = 600,
  zoom = 19
): string => {
  const key = encodeURIComponent(apiKey());
  return (
    `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}` +
    `&zoom=${zoom}&size=${width}x${height}&scale=2&maptype=satellite&key=${key}`
  );
};

/**
 * Google Street View Static image URL.
 *
 * Params applied by default:
 *   - `fov=80`      — natural human field of view.
 *   - `source=outdoor` — CRUCIAL: without this, Google frequently picks
 *     an INDOOR pano near the property (shop lobby, museum entrance,
 *     transit station) that reads as a dark blurry blob. This flag
 *     tells the API to only serve outdoor imagery — same street-level
 *     look and feel we care about.
 *   - `heading`     — optional, in degrees clockwise from north. When
 *     supplied, aims the camera in that direction. `useGoogleSlides`
 *     computes it via `bearingBetween(panoLat, panoLng, propertyLat,
 *     propertyLng)` so the camera faces the house rather than whatever
 *     the pano's default orientation was (often a wall / road).
 *   - `pitch=0`     — level horizon. Also default at Google's end but
 *     explicit here so it doesn't drift on API updates.
 */
export const streetViewImageUrl = (
  lat: number,
  lng: number,
  width = 800,
  height = 600,
  fov = 80,
  heading?: number
): string => {
  const key = encodeURIComponent(apiKey());
  const parts = [
    `location=${lat},${lng}`,
    `size=${width}x${height}`,
    `fov=${fov}`,
    `pitch=0`,
    `source=outdoor`,
  ];
  if (typeof heading === "number" && Number.isFinite(heading)) {
    parts.push(`heading=${Math.round(heading)}`);
  }
  parts.push(`key=${key}`);
  return `https://maps.googleapis.com/maps/api/streetview?${parts.join("&")}`;
};

/**
 * Google Street View metadata endpoint. FREE — never billed. Returns
 * JSON with `status: "OK"` when a panorama exists at the coords, or
 * `"ZERO_RESULTS"` / `"NOT_FOUND"` otherwise. Use to gate the actual
 * image call so rural / private-road addresses don't render the
 * generic "Sorry, we have no imagery here" placeholder.
 *
 * `source=outdoor` matches the image URL builder — otherwise metadata
 * could report OK for an indoor pano that the outdoor-filtered image
 * call would then decline, leaving us with the same generic placeholder.
 */
export const streetViewMetadataUrl = (lat: number, lng: number): string => {
  const key = encodeURIComponent(apiKey());
  return (
    `https://maps.googleapis.com/maps/api/streetview/metadata?location=${lat},${lng}` +
    `&source=outdoor&key=${key}`
  );
};

/**
 * Great-circle initial bearing from (fromLat,fromLng) to (toLat,toLng),
 * in degrees clockwise from north. Result is normalised to [0, 360).
 * Used to compute Street View `heading` so the camera faces the
 * property from wherever Google's chosen panorama actually sits.
 */
export const bearingBetween = (
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(fromLat);
  const φ2 = toRad(toLat);
  const Δλ = toRad(toLng - fromLng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return ((θ * 180) / Math.PI + 360) % 360;
};
